/**
 * The filter value sets the admin API accepts, spelled exactly as the Postgres
 * enums spell them.
 *
 * These are labels on real enum types. The Go handler rejects anything not in
 * these sets with a 400 rather than letting it reach the database, where it
 * would be `invalid input value for enum payment_status` and a 500 -- so a
 * dropdown that offers a value not listed here produces an error, not an empty
 * table. See validPaymentStatuses in internal/api/handler/admin/transactions.go.
 */

/**
 * SUCCESSFULL is misspelled in the database and is rendered that way everywhere.
 * Correcting it in the UI would teach ops a value that matches nothing they can
 * search for, and would put a badge on screen disagreeing with the filter
 * dropdown right above it.
 */
export const PAYMENT_STATUSES = [
  "INITIATED",
  "SUCCESSFULL",
  "SETTLED",
  "FAILED",
  "CANCELLED",
  "EXPIRED",
  "REFUNDED",
] as const;

export const PAYMENT_METHODS = ["QRIS", "VA", "Bank Transfer", "Debit / Credit Card"] as const;

/**
 * "Altura" is a label on the payment_provider enum (migration 000) even though
 * internal/models declares a constant only for DurianPay, so it is filterable
 * and rows carrying it exist.
 */
export const PAYMENT_PROVIDERS = ["DurianPay", "Altura"] as const;

/*
 * webhook_events. These are VARCHAR columns with no CHECK constraint, not
 * Postgres enums like the payment types above -- but the Go handler validates
 * them against the same sets and answers 400 for anything else, for the reason
 * that matters on an ops screen: a filter that quietly matches nothing reads as
 * "no failed webhooks today", which mid-incident is the wrong kind of good news.
 * See validWebhookStatuses in internal/api/handler/admin/webhook_events.go.
 */

export const WEBHOOK_STATUSES = ["received", "processed", "failed"] as const;

export const WEBHOOK_TYPES = ["payment", "settlement", "qris"] as const;

/** One source today. It is a filter because there will be a second provider. */
export const WEBHOOK_SOURCES = ["DURIANPAY"] as const;

/*
 * callback_deliveries. Unlike the webhook columns these DO have a CHECK
 * constraint (migration 007), so the five below are exhaustive by the schema's
 * own reckoning.
 */

export const CALLBACK_STATUSES = [
  "pending",
  "delivering",
  "delivered",
  "exhausted",
  "cancelled",
] as const;

/**
 * The merchant-facing event names, deliberately distinct from the internal
 * payment_status enum -- the public contract does not inherit the SUCCESSFULL
 * typo the database is stuck with. See models.CallbackEventFor; SETTLED,
 * REFUNDED and INITIATED produce no callback at all, which is why there are
 * four of these and seven payment statuses.
 */
export const CALLBACK_EVENT_TYPES = [
  "payment.succeeded",
  "payment.failed",
  "payment.cancelled",
  "payment.expired",
] as const;

/*
 * bridge_orders.status. A varchar with no CHECK, like the webhook columns, but
 * unlike settlements.status the set really is known: internal/models declares
 * all eight and the bridge write path is the only thing that writes them, so the
 * Go handler validates against exactly this list and answers 400 for anything
 * else.
 */
export const BRIDGE_ORDER_STATUSES = [
  "CREATED",
  "PAYMENT_LINK_SENT",
  "PAID",
  "POINTS_APPLIED",
  "FAILED",
  "CANCELLED",
  "EXPIRED",
  "REFUNDED",
] as const;
